import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbScriptTag } from "@/lib/breadcrumbSchema";
import { SCHEMA_LOCALBUSINESS_ID, SITE_URL } from "@/lib/entitySchema";
import { INDUSTRY_PAGES, type IndustryKey } from "@/lib/industriesData";
import {
  Wrench,
  Truck,
  Zap,
  Home as HomeIcon,
  Trees,
  Hammer,
  HardHat,
  Sparkle,
  Snowflake,
  Bug,
  PaintBucket,
  Car,
  Scissors,
  Stethoscope,
  Scale,
  Utensils,
  Dumbbell,
  Camera,
  Building2,
  Briefcase,
  ShoppingBag,
  Heart,
  GraduationCap,
  Wallet,
  Flame,
  Droplets,
  TreePine,
  Wind,
} from "lucide-react";

export const Route = createFileRoute("/industries")({
  head: () => ({
    meta: [
      {
        title: "Industries We Serve | Colorado Web Impressions",
      },
      {
        name: "description",
        content:
          "Colorado Web Impressions builds websites and SEO strategies for HVAC, plumbing, roofing, legal, medical, restaurants, retail, fitness and dozens more local industries across Colorado.",
      },
      {
        property: "og:title",
        content: "Industries We Serve | Colorado Web Impressions",
      },
      {
        property: "og:description",
        content:
          "We help Colorado trades and professional industries get more leads, calls, and booked jobs with web design, SEO, PPC and Google Maps optimization.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://coloradowebimpressions.com/industries" },
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/industries" }],
    scripts: [
      breadcrumbScriptTag([
        { name: "Home", path: "/" },
        { name: "Industries" },
      ]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/industries#collection`,
          url: `${SITE_URL}/industries`,
          name: "Industries We Serve — Colorado Web Impressions",
          description:
            "Colorado industries served by Colorado Web Impressions — HVAC, plumbing, roofing, legal, medical, restaurants, fitness, and more.",
          isPartOf: { "@id": `${SITE_URL}/#website` },
          about: { "@id": SCHEMA_LOCALBUSINESS_ID },
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: directoryEntries.length,
            itemListElement: directoryEntries.map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${SITE_URL}${p.path}`,
              name: p.name,
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": `${SITE_URL}/industries#service`,
          name: "Industry-Specific Digital Marketing",
          description:
            "Industry-tuned websites and SEO programs for Colorado trades and professional businesses — HVAC, plumbing, roofing, legal, medical, restaurants, fitness, retail, and more.",
          url: `${SITE_URL}/industries`,
          serviceType: "Digital Marketing",
          provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
          areaServed: { "@type": "State", name: "Colorado" },
        }),
      },
    ],
  }),
  component: IndustriesPage,
});

const SLUG_BY_NAME: Record<string, IndustryKey> = INDUSTRY_PAGES.reduce(
  (acc, p) => {
    acc[p.name] = p.slug;
    return acc;
  },
  {} as Record<string, IndustryKey>,
);

// Tiles whose dedicated marketing page lives at a top-level
// "/{industry}-marketing-colorado" route instead of /industries/$slug.
// Keep keys in sync with the tile `name` values in `categories` below.
const MARKETING_ROUTE_BY_NAME: Record<string, string> = {
  "Independent Auto Dealers": "/auto-dealer-marketing-colorado",
  "Junk Removal": "/junk-removal-marketing-colorado",
  "Moving Companies": "/moving-company-marketing-colorado",
  "Water Damage Restoration": "/restoration-marketing-colorado",
  "Commercial Contractors": "/commercial-contractor-marketing-colorado",
  "Medical Practices": "/medical-marketing-colorado",
  "HVAC": "/hvac-marketing-colorado",
  "Plumbing": "/plumbing-marketing-colorado",
  "Electrical": "/electrical-contractor-marketing-colorado",
  "Roofing": "/roofing-marketing-colorado",
  "Concrete & Excavation": "/concrete-marketing-colorado",
  "Cleaning Services": "/cleaning-marketing-colorado",
  "Snow Removal": "/snow-removal-marketing-colorado",
  "Pest Control": "/pest-control-marketing-colorado",
  "Painting": "/painting-marketing-colorado",
  "Fireplace & Chimney": "/fireplace-chimney-marketing-colorado",
  "Garage Doors": "/garage-door-marketing-colorado",
  "Tree Service": "/tree-service-marketing-colorado",
  "Accountants & CPAs": "/accountant-marketing-colorado",
  "Insurance Agencies": "/insurance-agency-marketing-colorado",
  "Real Estate": "/real-estate-broker-property-manager-marketing-colorado",
  "Tutors & Coaches": "/tutor-marketing-colorado",
  "Photographers": "/photographer-marketing-colorado",
  "Chiropractic": "/chiropractic-marketing-colorado",
  "Gyms & Fitness Studios": "/gym-marketing-colorado",
  "Salons & Spas": "/salon-spa-marketing-colorado",
  "Boutiques & Retail": "/boutique-retail-marketing-colorado",
  "Landscaping": "/landscaping-marketing-colorado",
  "General Contractors": "/general-contractor-marketing-colorado",
  "Law Firms": "/law-firm-marketing-colorado",
  "Medical & Dental": "/medical-dental-marketing-colorado",
  "Restaurants & Cafés": "/restaurant-marketing-colorado",
  "Auto Repair & Detailing": "/auto-repair-marketing-colorado",
  "Dog Trainers & K9 Schools": "/dog-trainer-marketing-colorado",
  "Personal Injury Attorneys": "/personal-injury-attorney-marketing-colorado",
  "Criminal Defense Attorneys": "/criminal-defense-attorney-marketing-colorado",
  "Family Law Attorneys": "/family-law-attorney-marketing-colorado",
  "Estate Planning Attorneys": "/estate-planning-attorney-marketing-colorado",
  "Dentists": "/dentist-marketing-colorado",
  "Orthodontists": "/orthodontist-marketing-colorado",
  "Physical Therapy Clinics": "/physical-therapy-marketing-colorado",
  "Med Spas": "/med-spa-marketing-colorado",
  "Plastic Surgeons": "/plastic-surgeon-marketing-colorado",
  "Dermatologists": "/dermatologist-marketing-colorado",
  "Ophthalmologists": "/ophthalmologist-marketing-colorado",
  "Commercial Roofing": "/commercial-roofing-marketing-colorado",
  "Steel Building Companies": "/steel-building-marketing-colorado",
  "Custom Home Builders": "/custom-home-builder-remodeler-marketing-colorado",
  "Trucking & Hauling Companies": "/trucking-marketing-colorado",
  "Equipment Rental Companies": "/equipment-rental-marketing-colorado",
  "Manufacturing Companies": "/manufacturing-marketing-colorado",
  "Franchise Businesses": "/franchise-marketing-colorado",
  "Mortgage Brokers": "/mortgage-broker-marketing-colorado",
};


const categories = [
  {
    name: "Home Services",
    blurb: "Trade businesses that live and die by phone calls and booked jobs.",
    industries: [
      { icon: Wrench, name: "HVAC" },
      { icon: Droplets, name: "Plumbing" },
      { icon: Zap, name: "Electrical" },
      { icon: HomeIcon, name: "Roofing" },
      { icon: Trees, name: "Landscaping" },
      { icon: Hammer, name: "General Contractors" },
      { icon: HardHat, name: "Concrete & Excavation" },
      { icon: Sparkle, name: "Cleaning Services" },
      { icon: Snowflake, name: "Snow Removal" },
      { icon: Bug, name: "Pest Control" },
      { icon: PaintBucket, name: "Painting" },
      { icon: Flame, name: "Fireplace & Chimney" },
      { icon: Wind, name: "Garage Doors" },
      { icon: TreePine, name: "Tree Service" },
      { icon: Truck, name: "Junk Removal" },
      { icon: Truck, name: "Moving Companies" },
      { icon: Droplets, name: "Water Damage Restoration" },
    ],
  },
  {
    name: "Professional Services",
    blurb: "Trust-driven businesses where local SEO and reputation drive growth.",
    industries: [
      { icon: Scale, name: "Law Firms" },
      { icon: Wallet, name: "Accountants & CPAs" },
      { icon: Briefcase, name: "Insurance Agencies" },
      { icon: Building2, name: "Real Estate" },
      { icon: GraduationCap, name: "Tutors & Coaches" },
      { icon: Camera, name: "Photographers" },
    ],
  },
  {
    name: "Health & Wellness",
    blurb: "Practices and studios that need a steady stream of new patients & clients.",
    industries: [
      { icon: Stethoscope, name: "Medical & Dental" },
      { icon: Heart, name: "Chiropractic" },
      { icon: Dumbbell, name: "Gyms & Fitness Studios" },
      { icon: Scissors, name: "Salons & Spas" },
    ],
  },
  {
    name: "Retail, Food & Auto",
    blurb: "Local storefronts that win when customers find you fast on Google.",
    industries: [
      { icon: Utensils, name: "Restaurants & Cafés" },
      { icon: ShoppingBag, name: "Boutiques & Retail" },
      { icon: Car, name: "Auto Repair & Detailing" },
      { icon: Car, name: "Independent Auto Dealers" },
    ],
  },
  {
    name: "Niche Local Services",
    blurb: "Specialty businesses with high search intent and low agency competition.",
    industries: [
      { icon: Heart, name: "Dog Trainers & K9 Schools" },
    ],
  },
  {
    name: "Legal Specialties",
    blurb: "Practice-area-specific marketing for Colorado law firms.",
    industries: [
      { icon: Scale, name: "Personal Injury Attorneys" },
      { icon: Scale, name: "Criminal Defense Attorneys" },
      { icon: Scale, name: "Family Law Attorneys" },
      { icon: Scale, name: "Estate Planning Attorneys" },
    ],
  },
  {
    name: "Medical Specialties",
    blurb: "Procedure-deep marketing for Colorado medical and aesthetic practices.",
    industries: [
      { icon: Stethoscope, name: "Medical Practices" },
      { icon: Stethoscope, name: "Dentists" },
      { icon: Stethoscope, name: "Orthodontists" },
      { icon: Heart, name: "Physical Therapy Clinics" },
      { icon: Sparkle, name: "Med Spas" },
      { icon: Stethoscope, name: "Plastic Surgeons" },
      { icon: Stethoscope, name: "Dermatologists" },
      { icon: Stethoscope, name: "Ophthalmologists" },
    ],
  },
  {
    name: "Commercial & Industrial",
    blurb: "B2B marketing for Colorado commercial trades, heavy industry, and franchise operators.",
    industries: [
      { icon: HardHat, name: "Commercial Contractors" },
      { icon: HomeIcon, name: "Commercial Roofing" },
      { icon: HardHat, name: "Steel Building Companies" },
      { icon: Hammer, name: "Custom Home Builders" },
      { icon: Truck, name: "Trucking & Hauling Companies" },
      { icon: Wrench, name: "Equipment Rental Companies" },
      { icon: Building2, name: "Manufacturing Companies" },
      { icon: Briefcase, name: "Franchise Businesses" },
      { icon: Wallet, name: "Mortgage Brokers" },
    ],
  },
];

const additionalCategories = [
  {
    name: "Home Improvement & Specialty Trades",
    blurb: "Colorado websites and online lead generation tailored to your customers and services.",
    industries: [
      { icon: Wrench, name: "Appliance Repair", path: "/appliance-repair-web-design-colorado" },
      { icon: Wrench, name: "Basement Finishing", path: "/basement-finishing-web-design-colorado" },
      { icon: Wrench, name: "Bathroom Remodeling", path: "/bathroom-remodeling-web-design-colorado" },
      { icon: Wrench, name: "Carpet Cleaning", path: "/carpet-cleaning-web-design-colorado" },
      { icon: Wrench, name: "Deck Builders", path: "/deck-builder-web-design-colorado" },
      { icon: Wrench, name: "Demolition", path: "/demolition-web-design-colorado" },
      { icon: Wrench, name: "Drywall Contractors", path: "/drywall-web-design-colorado" },
      { icon: Wrench, name: "Dumpster Rental", path: "/dumpster-rental-web-design-colorado" },
      { icon: Wrench, name: "Epoxy Flooring", path: "/epoxy-flooring-web-design-colorado" },
      { icon: Wrench, name: "Excavation", path: "/excavation-web-design-colorado" },
      { icon: Wrench, name: "Fencing Contractors", path: "/fencing-web-design-colorado" },
      { icon: Wrench, name: "Flooring Installers", path: "/flooring-web-design-colorado" },
      { icon: Wrench, name: "Foundation Repair", path: "/foundation-repair-web-design-colorado" },
      { icon: Wrench, name: "Garage Builders", path: "/garage-builder-web-design-colorado" },
      { icon: Wrench, name: "Gutter Companies", path: "/gutter-web-design-colorado" },
      { icon: Wrench, name: "Handyman Services", path: "/handyman-web-design-colorado" },
      { icon: Wrench, name: "Insulation Contractors", path: "/insulation-web-design-colorado" },
      { icon: Wrench, name: "Irrigation Companies", path: "/irrigation-web-design-colorado" },
      { icon: Wrench, name: "Kitchen Remodeling", path: "/kitchen-remodeling-web-design-colorado" },
      { icon: Wrench, name: "Locksmiths", path: "/locksmith-web-design-colorado" },
      { icon: Wrench, name: "Masonry Contractors", path: "/masonry-web-design-colorado" },
      { icon: Wrench, name: "Mold Remediation", path: "/mold-remediation-web-design-colorado" },
      { icon: Wrench, name: "Paving Contractors", path: "/paving-web-design-colorado" },
      { icon: Wrench, name: "Pole Barn Builders", path: "/pole-barn-builder-web-design-colorado" },
      { icon: Wrench, name: "Pool Service", path: "/pool-service-web-design-colorado" },
      { icon: Wrench, name: "Pressure Washing", path: "/pressure-washing-web-design-colorado" },
      { icon: Wrench, name: "Remodeling Contractors", path: "/remodeling-web-design-colorado" },
      { icon: Wrench, name: "Septic Services", path: "/septic-web-design-colorado" },
      { icon: Wrench, name: "Siding Contractors", path: "/siding-web-design-colorado" },
      { icon: Wrench, name: "Solar Installers", path: "/solar-web-design-colorado" },
      { icon: Wrench, name: "Well Drilling", path: "/well-drilling-web-design-colorado" },
      { icon: Wrench, name: "Window Cleaning", path: "/window-cleaning-web-design-colorado" },
    ],
  },
  {
    name: "Additional Healthcare & Patient Services",
    blurb: "Colorado websites and online lead generation tailored to your customers and services.",
    industries: [
      { icon: Stethoscope, name: "Acupuncture", path: "/acupuncture-web-design-colorado" },
      { icon: Stethoscope, name: "Addiction Treatment", path: "/addiction-treatment-web-design-colorado" },
      { icon: Stethoscope, name: "Allergists", path: "/allergist-web-design-colorado" },
      { icon: Stethoscope, name: "Audiology", path: "/audiology-web-design-colorado" },
      { icon: Stethoscope, name: "Cardiology", path: "/cardiology-web-design-colorado" },
      { icon: Stethoscope, name: "Endodontists", path: "/endodontist-web-design-colorado" },
      { icon: Stethoscope, name: "Fertility Clinics", path: "/fertility-clinic-web-design-colorado" },
      { icon: Stethoscope, name: "Hormone Therapy", path: "/hormone-therapy-web-design-colorado" },
      { icon: Stethoscope, name: "IV Therapy", path: "/iv-therapy-web-design-colorado" },
      { icon: Stethoscope, name: "Massage Therapy", path: "/massage-therapy-web-design-colorado" },
      { icon: Stethoscope, name: "Mental Health Practices", path: "/mental-health-web-design-colorado" },
      { icon: Stethoscope, name: "OB-GYN Practices", path: "/ob-gyn-web-design-colorado" },
      { icon: Stethoscope, name: "Optometry", path: "/optometry-web-design-colorado" },
      { icon: Stethoscope, name: "Oral Surgeons", path: "/oral-surgeon-web-design-colorado" },
      { icon: Stethoscope, name: "Pediatric Dentists", path: "/pediatric-dentist-web-design-colorado" },
      { icon: Stethoscope, name: "Pediatricians", path: "/pediatrician-web-design-colorado" },
      { icon: Stethoscope, name: "Periodontists", path: "/periodontist-web-design-colorado" },
      { icon: Stethoscope, name: "Podiatry", path: "/podiatry-web-design-colorado" },
      { icon: Stethoscope, name: "Primary Care", path: "/primary-care-web-design-colorado" },
      { icon: Stethoscope, name: "Urgent Care", path: "/urgent-care-web-design-colorado" },
      { icon: Stethoscope, name: "Weight Loss Clinics", path: "/weight-loss-clinic-web-design-colorado" },
    ],
  },
  {
    name: "Additional Legal & Financial Services",
    blurb: "Colorado websites and online lead generation tailored to your customers and services.",
    industries: [
      { icon: Scale, name: "Bankruptcy Lawyers", path: "/bankruptcy-lawyer-web-design-colorado" },
      { icon: Scale, name: "Business Lawyers", path: "/business-lawyer-web-design-colorado" },
      { icon: Scale, name: "DUI Lawyers", path: "/dui-lawyer-web-design-colorado" },
      { icon: Scale, name: "Employment Lawyers", path: "/employment-lawyer-web-design-colorado" },
      { icon: Scale, name: "Financial Advisors", path: "/financial-advisor-web-design-colorado" },
      { icon: Scale, name: "Immigration Lawyers", path: "/immigration-lawyer-web-design-colorado" },
      { icon: Scale, name: "Intellectual Property Lawyers", path: "/ip-lawyer-web-design-colorado" },
      { icon: Scale, name: "Real Estate Lawyers", path: "/real-estate-lawyer-web-design-colorado" },
      { icon: Scale, name: "Tax Attorneys", path: "/tax-attorney-web-design-colorado" },
      { icon: Scale, name: "Workers’ Compensation Lawyers", path: "/workers-comp-lawyer-web-design-colorado" },
    ],
  },
  {
    name: "Community, Events & Personal Services",
    blurb: "Colorado websites and online lead generation tailored to your customers and services.",
    industries: [
      { icon: Briefcase, name: "Barbershops", path: "/barbershop-web-design-colorado" },
      { icon: Briefcase, name: "Catering Companies", path: "/catering-web-design-colorado" },
      { icon: Briefcase, name: "Daycare Centers", path: "/daycare-web-design-colorado" },
      { icon: Briefcase, name: "Event Venues", path: "/event-venue-web-design-colorado" },
      { icon: Briefcase, name: "Personal Trainers", path: "/personal-trainer-web-design-colorado" },
      { icon: Briefcase, name: "Pet Grooming", path: "/pet-grooming-web-design-colorado" },
      { icon: Briefcase, name: "Tattoo Shops", path: "/tattoo-shop-web-design-colorado" },
      { icon: Briefcase, name: "Veterinary Practices", path: "/veterinary-web-design-colorado" },
      { icon: Briefcase, name: "Videographers", path: "/videographer-web-design-colorado" },
      { icon: Briefcase, name: "Wedding Photographers", path: "/wedding-photographer-web-design-colorado" },
      { icon: Briefcase, name: "Yoga Studios", path: "/yoga-studio-web-design-colorado" },
    ],
  },
];

const allCategories = [...categories, ...additionalCategories];
const directoryEntries = allCategories.flatMap((category) => category.industries.map((industry) => ({
  name: industry.name,
  path: "path" in industry ? String(industry.path) : MARKETING_ROUTE_BY_NAME[industry.name] ?? `/industries/${SLUG_BY_NAME[industry.name]}`,
})));

function IndustriesPage() {
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const filteredCategories = allCategories.map((category) => ({ ...category, industries: category.industries.filter((industry) => `${industry.name} ${category.name}`.toLowerCase().includes(query)) })).filter((category) => category.industries.length > 0);
  const resultCount = filteredCategories.reduce((count, category) => count + category.industries.length, 0);
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Colorado Industries We Serve"
        title={
          <>
            We build websites and SEO that work for{" "}
            <span className="text-gradient-gold">your industry</span>
          </>
        }
        description="From trade businesses to law firms, restaurants to medical practices — we tailor every site, page, and campaign to how customers actually search for your services in Colorado."
      >
        <div className="flex justify-center">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Industries" }]} />
        </div>
      </PageHero>

      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 space-y-20">
          <div className="mx-auto max-w-2xl space-y-3">
            <label htmlFor="industry-search" className="block text-lg font-bold text-foreground">Find your industry</label>
            <div className="flex items-center gap-2">
              <Input id="industry-search" type="search" placeholder="Search trades, practices or businesses" value={search} onChange={(event) => setSearch(event.target.value)} className="h-12" />
              {search && <Button variant="outline" onClick={() => setSearch("")}>Clear</Button>}
            </div>
            <p aria-live="polite" className="text-sm text-muted-foreground">{resultCount} industries</p>
            {resultCount === 0 && <p className="text-foreground">No matching industry. Contact us about your Colorado business.</p>}
          </div>
          {filteredCategories.map((cat) => (
            <div key={cat.name}>
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-3xl font-extrabold tracking-normal text-foreground sm:text-4xl">
                  {cat.name}
                </h2>
                <p className="mt-3 text-muted-foreground">{cat.blurb}</p>
              </div>
              <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {cat.industries.map((i) => {
                  const slug = SLUG_BY_NAME[i.name];
                  const marketingRoute = "path" in i ? String(i.path) : MARKETING_ROUTE_BY_NAME[i.name];
                  const tileClass =
                    "group flex flex-col items-center gap-3 rounded-lg border border-border bg-card p-4 sm:p-6 text-center shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50";
                  const kebab = i.name
                    .toLowerCase()
                    .replace(/&/g, "and")
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "");
                  const hasPage = Boolean(slug || marketingRoute);
                  const caption = hasPage ? "See details →" : "Get a quote →";
                  const prefillMessage = `I run a ${i.name} business in Colorado and would like to discuss marketing, SEO, and a new website. Please reach out with next steps.`;
                  const inner = (
                    <>
                      <div className="grid h-12 w-12 place-items-center rounded-lg bg-secondary text-brand-gold-deep transition-smooth group-hover:bg-gradient-gold group-hover:text-brand-black">
                        <i.icon className="h-6 w-6" />
                      </div>
                      <div className="text-sm font-bold text-foreground">{i.name}</div>
                      <div className="text-xs font-semibold uppercase tracking-normal text-brand-gold-deep">
                        {caption}
                      </div>
                    </>
                  );
                  if (marketingRoute) {
                    return (
                      <Link
                        key={i.name}
                        to={marketingRoute}
                        className={tileClass}
                      >
                        {inner}
                      </Link>
                    );
                  }
                  if (slug) {
                    return (
                      <Link
                        key={i.name}
                        to="/industries/$industrySlug"
                        params={{ industrySlug: slug }}
                        className={tileClass}
                      >
                        {inner}
                      </Link>
                    );
                  }

                  return (
                    <Link
                      key={i.name}
                      to="/contact"
                      search={{ topic: kebab, message: prefillMessage }}
                      className={tileClass}
                    >
                      {inner}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="border-t border-border py-10 text-center">
            <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
              Don't see your industry?
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              If you run a Colorado business that depends on local customers finding you online, we
              can help. Get a free strategy call and we'll show you exactly what's possible.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-full bg-gradient-gold px-6 py-3 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                Get a free strategy call
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
